import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { CLAIM } from '@constants/index'
import { DateUtil } from '@utils/dateUtil'
import { isGatewayTimeout } from '@utils/errorUtil'
import { SubmitErrorResponse } from '@typesApp/SubmitResponse'
import { ClaimRecord, ClaimRecordStatus, NullifyOptions } from '@typesApp/ClaimRecord'
import { claimRecordsApi, useNullifyClaimMutation } from '@redux/queries/claimRecords'
import { usePollClaimRecord } from './usePollClaimRecord'

type UseNullifyClaimRecordReturn = {
  shouldNullify: (claimRecordStatus: ClaimRecordStatus) => boolean
  nullifyClaimRecord: (options: NullifyOptions) => void
  busy: boolean
}

const VALID_STATUS_LIST = [
  ClaimRecordStatus.READY_TO_CLAIM,
  ClaimRecordStatus.QUOTE_REQUIRED,
  ClaimRecordStatus.CLAIM_NULLIFIED,
  ClaimRecordStatus.FOR_MANUAL_NULLIFY,
  ClaimRecordStatus.CLOSED,
  ClaimRecordStatus.RESTRICTED,
]

// Poll until RESTRICTED or nullified / closed if last nullify attempt
const VALID_STATUS_LIST_LAST_NULLIFY = [
  ClaimRecordStatus.CLAIM_NULLIFIED,
  ClaimRecordStatus.FOR_MANUAL_NULLIFY,
  ClaimRecordStatus.CLOSED,
  ClaimRecordStatus.RESTRICTED,
]

export const useNullifyClaimRecord = (): UseNullifyClaimRecordReturn => {
  const dispatch = useDispatch()
  const [startTime, setStartTime] = useState<Date>()
  const [options, setOptions] = useState<NullifyOptions>()
  const [busy, setBusy] = useState(false)
  const [nullify] = useNullifyClaimMutation()
  const pollClaimRecord = usePollClaimRecord()

  useEffect(() => {
    if (startTime) {
      nullifyClaimWithRetry()
    }
  }, [startTime])

  const nullifyClaimWithRetry = () => {
    setBusy(true)

    // if nullify is already sent then poll claim record
    if (options?.claimRecordStatus === ClaimRecordStatus.CLAIM_NULLIFY_SENT) {
      setTimeout(pollClaimRecordForResult, CLAIM.POLLING_INTERVAL_MS)
    } else {
      triggerNullify()
    }
  }

  const triggerNullify = () => {
    if (!options) return

    nullify(options)
      .unwrap()
      .then(result => {
        if (isResultReady(result?.claimRecord)) {
          handleResult(result?.claimRecord)
        } else {
          setTimeout(pollClaimRecordForResult, CLAIM.POLLING_INTERVAL_MS)
        }
      })
      .catch(error => {
        handleNullifyError(error)
      })
  }

  const isResultReady = (claimRecord?: ClaimRecord): boolean => {
    if (!claimRecord || !startTime) return false

    // successfully nullified
    const validStatusList = options?.isLastAttempt ? VALID_STATUS_LIST_LAST_NULLIFY : VALID_STATUS_LIST
    const isValidStatus = validStatusList.includes(claimRecord?.status)

    const isError = [ClaimRecordStatus.CLAIM_NULLIFY_REJECTED].includes(claimRecord?.status)

    return isValidStatus || isError || DateUtil.isMaxTimeExceeded({ startTime, maxTime: CLAIM.MAX_NULLIFY_WAIT_TIME })
  }

  const handleResult = (claimRecord: ClaimRecord) => {
    const validStatusList = options?.isLastAttempt ? VALID_STATUS_LIST_LAST_NULLIFY : VALID_STATUS_LIST
    const isValidStatus = validStatusList.includes(claimRecord?.status)

    if (isValidStatus) {
      afterNullify(claimRecord.status)
      options?.onComplete?.()
    } else {
      options?.onError?.()
    }

    options?.onFinally?.()
    setBusy(false)
  }

  const afterNullify = (claimRecordStatus: ClaimRecordStatus) => {
    dispatch(claimRecordsApi.util.resetApiState())
  }

  const handleNullifyError = (error: SubmitErrorResponse) => {
    if (isGatewayTimeout(error?.status)) {
      setTimeout(pollClaimRecordForResult, CLAIM.POLLING_INTERVAL_MS)
    } else {
      setBusy(false)
      options?.onError?.(error)
    }
  }

  const handleError = (error?: unknown) => {
    setBusy(false)
    options?.onError?.(error)
  }

  const pollClaimRecordForResult = () => {
    pollClaimRecord(isResultReady, handleResult, handleError)
  }

  const shouldNullify = (claimRecordStatus: ClaimRecordStatus): boolean => {
    return claimRecordStatus === ClaimRecordStatus.CLAIM_ACCEPTED || claimRecordStatus === ClaimRecordStatus.CLAIM_RETRY
  }

  const nullifyClaimRecord = (options: NullifyOptions) => {
    setOptions(options)
    setStartTime(new Date())
  }

  return {
    shouldNullify,
    nullifyClaimRecord,
    busy,
  }
}
